/** User-facing appearance preference (Notion-style: system / light / dark). */
export const THEME_PREFERENCES = ['system', 'light', 'dark'] as const

export type ThemePreference = (typeof THEME_PREFERENCES)[number]

/** Resolved palette after applying system preference. */
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'cse.theme'

export function isThemePreference(value: string | null | undefined): value is ThemePreference {
  return THEME_PREFERENCES.some((option) => option === value)
}

export function readStoredThemePreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY)
    if (isThemePreference(raw)) return raw
  } catch {
    // private mode / blocked storage
  }
  return 'system'
}

export function writeStoredThemePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // ignore
  }
}

export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function resolveTheme(preference: ThemePreference, prefersDark = systemPrefersDark()): ResolvedTheme {
  if (preference === 'dark') return 'dark'
  if (preference === 'light') return 'light'
  return prefersDark ? 'dark' : 'light'
}

/**
 * Apply the resolved theme to `<html>`: toggle `.dark` and native `color-scheme`
 * so scrollbars/form controls match.
 */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
}
